import React, {
  Children,
  FC,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { A11y, SwiperOptions, Pagination, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.min.css';
import { Button } from '../button';

// Interface

export interface CardFeedProps {
  propName?: string;
}

interface StyledSwiperProps {
  breakpoints?: SwiperOptions;
  $numberOfChildren?: number;
}

// Styles

const StyledCardFeed = styled.div`
  ${({ theme: { space, colors, media } }) => css`
    display: flex;
    flex-direction: column;
    height: 100%;
    .swiper-slide {
      > * {
        height: 100%;
      }
    }
  `};
`;

const StyledNavigation = styled.div`
  ${({ theme: { space, colors, maxMedia } }) => css`
    display: flex;
    gap: ${space.xSmall};
    position: absolute;
    right: 0;
    bottom: -32px;

    @media (max-width: ${maxMedia.medium}) {
      display: none;
    }
  `}
`;

const StyledSwiper = styled(Swiper)<StyledSwiperProps>`
  ${({
    theme: { space, maxMedia, colors, global, media },
    breakpoints,
    $numberOfChildren,
  }) => {
    let carouselStyles = '';

    if (breakpoints) {
      Object.keys(breakpoints).forEach((key, index) => {
        const breakpoint = `${key}px`;
        const { slidesPerView } = breakpoints[key];
        if (slidesPerView && $numberOfChildren && slidesPerView !== 'auto') {
          carouselStyles += `
            @media (min-width: ${breakpoint}) {
              ${StyledNavigation} {
                display: ${$numberOfChildren > slidesPerView ? 'flex' : 'none'}
              }
            }`;
        }
      });
    }

    return css`
      ${carouselStyles}
      max-width: 100%;
      overflow: visible;
      position: relative;

      .swiper-slide {
        height: auto;

        max-width: 100%;
        margin-right: ${space.xSmall};

        @media (min-width: 600px) {
          max-width: 50%;
          margin-right: ${space.medium};
        }

        @media (min-width: 1024px) {
          max-width: 33%;
        }

        @media (min-width: ${media.xLarge}) {
          max-width: 352px;
        }

        .gatsby-image-wrapper-constrained {
          width: 100%;
        }
      }

      .swiper-pagination-progressbar {
        position: relative;
        background: ${colors.neutralWhite};
        overflow: hidden;
        margin-top: ${space.large};
        margin-bottom: ${space.small};

        @media (min-width: ${media.medium}) {
          margin-bottom: 0;
        }

        .swiper-pagination-progressbar-fill {
          background: ${colors.brandTwo100};
        }
      }

      // Desktop only
      @media (min-width: ${media.medium}) {
        .swiper-pagination-progressbar {
          width: calc(100% - 176px);
        }
      }

      // Mobile < styles

      @media (max-width: ${maxMedia.medium}) {
        padding-right: ${space.large};

        .swiper-pagination-progressbar {
          width: calc(100% + ${space.large});
        }
      }
    `;
  }}
`;

// JSX

export const CardFeed: FC<React.PropsWithChildren<CardFeedProps>> = ({
  children,
  ...rest
}) => {
  const [controlledSwiper, setControlledSwiper] = useState<any>(null);
  const hasMount = useRef(false);
  const prevNav = useRef<HTMLButtonElement>(null);
  const nextNav = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (controlledSwiper && !hasMount.current) {
      controlledSwiper.init();
      hasMount.current = true;

      setTimeout(() => {
        controlledSwiper.update();
      }, 300);
    }
  }, [controlledSwiper]);

  const opts: SwiperOptions = {
    watchOverflow: true,
    loop: false,
    pagination: {
      type: 'progressbar',
    },
    navigation: {
      prevEl: prevNav.current,
      nextEl: nextNav.current,
    },
    preventInteractionOnTransition: true,
    spaceBetween: 16,

    slidesPerView: 1,
    breakpoints: {
      600: {
        slidesPerView: 2,
        slidesPerGroup: 2,
        spaceBetween: 32,
      },
      1024: {
        slidesPerView: 3,
        slidesPerGroup: 3,
        spaceBetween: 32,
      },
    },
  };

  if (!children || Children.count(children) < 1) {
    return null;
  }

  return (
    <StyledCardFeed {...rest}>
      <StyledSwiper
        onSwiper={setControlledSwiper}
        wrapperTag="div"
        modules={[Pagination, A11y, Navigation]}
        {...opts}
        breakpoints={opts?.breakpoints}
        $numberOfChildren={Children.count(children)}
        className="carousel-swiper"
      >
        {React.Children.map(children, (slide, i) => (
          <SwiperSlide key={`swiper-${i}`} tag="div">
            {React.cloneElement(slide as ReactElement, {})}
          </SwiperSlide>
        ))}

        {/* Swiper navigation controls */}

        <StyledNavigation>
          <Button
            arrowLeft
            variant="secondaryOutline"
            ref={prevNav}
            a11yTitle="Previous"
          />
          <Button
            arrowRight
            variant="secondaryOutline"
            ref={nextNav}
            a11yTitle="Next"
          />
        </StyledNavigation>
      </StyledSwiper>
    </StyledCardFeed>
  );
};

export default CardFeed;
